/**
 * A tiny, dependency-free Source Map v3 reader — just enough to answer "what
 * ORIGINAL location does this generated position map to?", so a slow-frame offset
 * in a minified bundle can be shown as the real source. We decode only what we
 * need (a one-way generated → original lookup); we do NOT encode, and we do NOT
 * support the rare sectioned "index map" (callers fall back to the minified slice).
 *
 * The format: JSON with `sources`, `sourcesContent`, `names`, and `mappings` — a
 * string of `;`-separated generated lines, each `,`-separated into Base64-VLQ
 * segments of [genColumn, sourceIdx, origLine, origColumn, nameIdx]. All values are
 * DELTA-encoded; genColumn resets each line, the other four accumulate across the
 * whole string.
 */

const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

/** Decode a Base64-VLQ run into its signed integers. Deltas are small (distance to
 *  the previous mapping), so 32-bit assembly is safe here. */
function* vlqs(s: string): Generator<number> {
    let value = 0, shift = 0;
    for (let i = 0; i < s.length; i++) {
        const digit = B64.indexOf(s[i]);
        if (digit < 0) return; // invalid — stop this run
        value |= (digit & 31) << shift;
        if (digit & 32) { shift += 5; continue; } // continuation bit set → more digits
        const negative = value & 1;
        value >>= 1;
        yield negative ? -value : value;
        value = 0; shift = 0;
    }
}

interface Segment {
    /** column in the generated line (absolute) */
    readonly genColumn: number;
    /** index into `sources`, or -1 for a "no original here" segment */
    readonly sourceIndex: number;
    /** 0-based line in the original source */
    readonly originalLine: number;
    readonly originalColumn: number;
    /** index into `names`, or -1 */
    readonly nameIndex: number;
}

export interface DecodedSourceMap {
    readonly sources: readonly (string | null)[];
    readonly sourcesContent: readonly (string | null)[] | null;
    readonly names: readonly string[];
    readonly sourceRoot: string;
    /** segments per GENERATED line (0-based), sorted by genColumn */
    readonly lines: readonly Segment[][];
}

export interface OriginalPosition {
    /** resolved source path (sourceRoot + sources[i]) */
    readonly source: string;
    /** 1-based original line (display-friendly) */
    readonly line: number;
    /** 0-based original column */
    readonly column: number;
    readonly name: string | null;
    /** the original source text, if the map embedded `sourcesContent` for it */
    readonly sourceContent: string | null;
}

interface RawSourceMap {
    version?: number;
    sources?: (string | null)[];
    sourcesContent?: (string | null)[];
    names?: string[];
    mappings?: string;
    sourceRoot?: string;
    sections?: unknown[];
}

/** Parse + decode a v3 map's JSON. Returns null for malformed input or an index map. */
export function decodeSourceMap(json: string): DecodedSourceMap | null {
    let raw: RawSourceMap;
    try { raw = JSON.parse(json) as RawSourceMap; } catch { return null; }
    if (!raw || typeof raw.mappings !== "string" || !Array.isArray(raw.sources)) return null;
    if (Array.isArray(raw.sections)) return null; // sectioned index map — unsupported (v1)

    const lines: Segment[][] = [];
    // cumulative across the WHOLE mappings string (only genColumn resets per line)
    let sourceIndex = 0, originalLine = 0, originalColumn = 0, nameIndex = 0;
    for (const lineStr of raw.mappings.split(";")) {
        const segs: Segment[] = [];
        let genColumn = 0;
        if (lineStr) {
            for (const segStr of lineStr.split(",")) {
                if (!segStr) continue;
                const f = [...vlqs(segStr)];
                if (!f.length) continue;
                genColumn += f[0];
                if (f.length < 4) { // only a generated column → no original mapping here
                    segs.push({ genColumn, sourceIndex: -1, originalLine: 0, originalColumn: 0, nameIndex: -1 });
                    continue;
                }
                sourceIndex += f[1];
                originalLine += f[2];
                originalColumn += f[3];
                if (f.length >= 5) nameIndex += f[4];
                segs.push({ genColumn, sourceIndex, originalLine, originalColumn, nameIndex: f.length >= 5 ? nameIndex : -1 });
            }
        }
        lines.push(segs);
    }
    return {
        sources: raw.sources,
        sourcesContent: Array.isArray(raw.sourcesContent) ? raw.sourcesContent : null,
        names: Array.isArray(raw.names) ? raw.names : [],
        sourceRoot: raw.sourceRoot || "",
        lines,
    };
}

function joinSourceRoot(root: string, source: string): string {
    if (!root || /^[a-z][a-z0-9+.-]*:\/\//i.test(source) || source.startsWith("/")) return source;
    return root.endsWith("/") ? root + source : root + "/" + source;
}

/** The original position for a GENERATED position (genLine 0-based, genColumn 0-based),
 *  or null if nothing maps at/before it on that line. */
export function originalPositionFor(map: DecodedSourceMap, genLine: number, genColumn: number): OriginalPosition | null {
    const segs = map.lines[genLine];
    if (!segs || !segs.length) return null;
    // the last segment whose genColumn is <= the target (segments are column-sorted)
    let best: Segment | null = null;
    for (const s of segs) {
        if (s.genColumn <= genColumn) best = s;
        else break;
    }
    if (!best || best.sourceIndex < 0) return null;
    const rawSource = map.sources[best.sourceIndex];
    if (rawSource == null) return null;
    return {
        source: joinSourceRoot(map.sourceRoot, rawSource),
        line: best.originalLine + 1,
        column: best.originalColumn,
        name: best.nameIndex >= 0 ? (map.names[best.nameIndex] ?? null) : null,
        sourceContent: map.sourcesContent?.[best.sourceIndex] ?? null,
    };
}

/** Char offset in `text` → 0-based { line, column } (UTF-16 code units, matching sourcemap columns). */
export function offsetToLineColumn(text: string, offset: number): { line: number; column: number } {
    const end = Math.max(0, Math.min(offset, text.length));
    let line = 0, lineStart = 0;
    for (let i = 0; i < end; i++) {
        if (text.charCodeAt(i) === 10 /* \n */) { line++; lineStart = i + 1; }
    }
    return { line, column: end - lineStart };
}

/** 1-based line + 0-based column → char offset in `text` (inverse of offsetToLineColumn). */
export function lineColumnToOffset(text: string, line: number, column: number): number {
    let idx = 0;
    for (let cur = 1; cur < line; cur++) {
        const nl = text.indexOf("\n", idx);
        if (nl < 0) return text.length;
        idx = nl + 1;
    }
    return Math.min(text.length, idx + column);
}

function base64ToUtf8(b64: string): string {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder().decode(bytes);
}

/** The raw URL from the file's trailing `//# sourceMappingURL=` comment (last wins), or null. */
function readSourceMappingURL(source: string): string | null {
    const re = /\/\/[#@]\s*sourceMappingURL=([^\s'"]+)[ \t]*$/gm;
    let m: RegExpExecArray | null, url: string | null = null;
    while ((m = re.exec(source))) url = m[1];
    return url;
}

/** Read the map JSON from the `//# sourceMappingURL=` comment, but ONLY when it's an
 *  INLINE data: URI (self-contained — no extra fetch, no CSP problem). Null for an
 *  external URL (see {@link readExternalSourceMapUrl}) or no comment. */
export function readInlineSourceMap(source: string): string | null {
    const url = readSourceMappingURL(source);
    if (!url || !url.startsWith("data:")) return null;
    const comma = url.indexOf(",");
    if (comma < 0) return null;
    const meta = url.slice("data:".length, comma);
    const data = url.slice(comma + 1);
    try {
        return /;base64/i.test(meta) ? base64ToUtf8(data) : decodeURIComponent(data);
    } catch {
        return null;
    }
}

/** The EXTERNAL map URL (a non-`data:` `sourceMappingURL`) made absolute against the
 *  script's URL, or null when the reference is inline, absent, or unresolvable.
 *  Fetching it (subject to CORS/CSP) is the caller's job. */
export function readExternalSourceMapUrl(source: string, scriptUrl: string): string | null {
    const url = readSourceMappingURL(source);
    if (!url || url.startsWith("data:")) return null;
    try { return new URL(url, scriptUrl).href; } catch { return null; }
}
